import React, { useEffect, useState } from 'react';
import { orderService, Order, orderStatusConfig, OrderStatus } from '../../services';
import { selectionService, Selection } from '../../services/selection';
import { useNavigate, useLocation } from 'react-router-dom';
import { Share2, ShoppingBag, List, ChevronLeft, ChevronRight, Filter, RefreshCw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const OrdersPage: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [selections, setSelections] = useState<Selection[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const location = useLocation();
    const [activeTab, setActiveTab] = useState<'orders' | 'selections'>(
        (location.state as any)?.tab === 'selections' ? 'selections' : 'orders'
    );
    const [sharingId, setSharingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (activeTab === 'orders') {
            loadOrders();
        } else {
            loadSelections();
        }
    }, [page, activeTab, statusFilter]); // Added statusFilter dependency for orders

    const loadOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            // Note: If API supports filtering by status in the same endpoint, we should pass it here.
            // Currently filtering client-side for status based on previous code, but typically this should be server-side.
            // Assuming getOrders supports pagination only for now.
            const response = await orderService.getOrders(page, 10);
            setOrders(response.orders || []);
            setTotalPages(response.total_pages || 1);
        } catch (err) {
            console.error('Error loading orders:', err);
            setError('خطا در دریافت لیست سفارشات. لطفا مجددا تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    const loadSelections = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await selectionService.getHistory({ page, page_size: 10 });
            const data = response.data?.data;
            if (Array.isArray(data)) {
                setSelections(data);
            } else {
                setSelections([]);
            }
            setTotalPages(response.data.meta?.total_pages || 1);
        } catch (err) {
            console.error('Error loading selections:', err);
            setError('خطا در دریافت لیست‌های منتخب. لطفا مجددا تلاش کنید.');
            setSelections([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab: 'orders' | 'selections') => {
        setActiveTab(tab);
        setPage(1);
        setStatusFilter('');
    };

    const handleShare = async (e: React.MouseEvent, id: string, type: 'order' | 'selection') => {
        e.stopPropagation();
        setSharingId(id);
        try {
            let imageUrl: string;

            if (type === 'order') {
                const response = await orderService.generateShareImage(id);
                imageUrl = response.image_url;
            } else {
                const response = await selectionService.generateShareImage(id);
                imageUrl = response.image_url;
            }

            if (navigator.share) {
                try {
                    const imageBlob = await fetch(imageUrl).then(r => r.blob());
                    const file = new File([imageBlob], `${type}-${id}.png`, { type: 'image/png' });

                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: type === 'order' ? 'سفارش من' : 'لیست منتخب من',
                            text: type === 'order' ? 'جزئیات سفارش من در راز گلد' : 'لیست محصولات انتخابی من در راز گلد',
                        });
                    } else {
                        await navigator.share({
                            title: type === 'order' ? 'سفارش من' : 'لیست منتخب من',
                            text: type === 'order' ? 'جزئیات سفارش من در راز گلد' : 'لیست محصولات انتخابی من در راز گلد',
                            url: imageUrl
                        });
                    }
                } catch (e) {
                     console.log("Share failed or cancelled", e);
                }
            } else {
                window.open(imageUrl, '_blank');
                toast.success('تصویر در تب جدید باز شد');
            }
        } catch (error) {
            console.error('Error sharing:', error);
            toast.error('خطا در ایجاد تصویر اشتراک‌گذاری');
        } finally {
            setSharingId(null);
        }
    };

    const formatPrice = (price: string | number) => {
        return new Intl.NumberFormat('fa-IR').format(typeof price === 'string' ? parseFloat(price) : price);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const filteredOrders = statusFilter
        ? orders.filter((o) => o.status === statusFilter)
        : orders;

    const statusFilters = [
        { value: '', label: 'همه' },
        { value: 'pending', label: 'در انتظار' },
        { value: 'confirmed', label: 'تایید شده' },
        { value: 'processing', label: 'در حال پردازش' },
        { value: 'shipped', label: 'ارسال شده' },
        { value: 'delivered', label: 'تحویل شده' },
        { value: 'cancelled', label: 'لغو شده' },
    ];

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">مدیریت سفارشات</h1>
                    <p className="text-gray-500 text-sm">پیگیری سفارشات و لیست‌های منتخب شما</p>
                </div>
                <button 
                    onClick={() => activeTab === 'orders' ? loadOrders() : loadSelections()}
                    className="self-start md:self-center p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="بروزرسانی لیست"
                    title="بروزرسانی"
                >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </header>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-full md:w-fit" role="tablist">
                <button
                    role="tab"
                    aria-selected={activeTab === 'selections'}
                    onClick={() => handleTabChange('selections')}
                    className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        activeTab === 'selections' 
                            ? 'bg-white text-primary shadow-sm' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                    }`}
                >
                    <List className="w-4 h-4" />
                    <span>لیست‌های من</span>
                </button>
                <button
                    role="tab"
                    aria-selected={activeTab === 'orders'}
                    onClick={() => handleTabChange('orders')}
                    className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        activeTab === 'orders' 
                            ? 'bg-white text-primary shadow-sm' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                    }`}
                >
                    <ShoppingBag className="w-4 h-4" />
                    <span>سفارشات</span>
                </button>
            </div>

            {/* Status Filters (Orders Only) */}
            {activeTab === 'orders' && (
                <div className="mb-6 relative">
                    <div className="flex items-center gap-2 mb-2 text-gray-500 text-sm md:hidden">
                        <Filter className="w-4 h-4" />
                        <span>فیلتر وضعیت:</span>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                        {statusFilters.map((filter) => (
                            <button
                                key={filter.value}
                                onClick={() => setStatusFilter(filter.value)}
                                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                                    statusFilter === filter.value
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{error}</p>
                    <button 
                        onClick={() => activeTab === 'orders' ? loadOrders() : loadSelections()}
                        className="mr-auto text-sm font-medium underline hover:text-red-800"
                    >
                        تلاش مجدد
                    </button>
                </div>
            )}

            {/* Content List */}
            <div className="space-y-4">
                {loading ? (
                    // Loading Skeletons
                    [...Array(3)].map((_, i) => (
                        <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm animate-pulse">
                            <div className="flex flex-col md:flex-row md:items-center gap-4">
                                <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0" />
                                <div className="flex-1 space-y-3">
                                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                                </div>
                                <div className="h-8 bg-gray-200 rounded w-24" />
                            </div>
                        </div>
                    ))
                ) : activeTab === 'orders' ? (
                    filteredOrders.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-900 mb-2">سفارشی یافت نشد</h3>
                            <p className="text-gray-500 mb-6 max-w-xs mx-auto">
                                {statusFilter ? 'سفارشی با این فیلتر وجود ندارد.' : 'شما هنوز هیچ سفارشی ثبت نکرده‌اید.'}
                            </p>
                            <button
                                onClick={() => navigate('/products')}
                                className="bg-primary text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-light transition-colors shadow-lg shadow-primary/20"
                            >
                                مشاهده محصولات
                            </button>
                        </div>
                    ) : (
                        filteredOrders.map((order) => {
                            const statusConfig = orderStatusConfig[order.status];
                            return (
                                <div
                                    key={order.id}
                                    className="group bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-all duration-300 cursor-pointer relative overflow-hidden"
                                    onClick={() => navigate(`/orders/${order.id}`)}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center gap-4 relative z-10">
                                        {/* Icon/Status Indicator */}
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${statusConfig?.bgColor || 'bg-gray-100'}`}>
                                            <ShoppingBag className={`w-6 h-6 ${statusConfig?.color || 'text-gray-500'}`} />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <h3 className="font-bold text-gray-900 truncate">
                                                    سفارش <span className="font-mono text-lg ml-1">#{order.id.slice(0, 8)}</span>
                                                </h3>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig?.bgColor} ${statusConfig?.color}`}>
                                                    {statusConfig?.label}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span>{formatDate(order.created_at)}</span>
                                                <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                                <span>{order.items?.length || 0} قلم کالا</span>
                                            </div>
                                        </div>

                                        {/* Price & Action */}
                                        <div className="flex items-center justify-between md:justify-end gap-4 mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-50">
                                            <div className="font-bold text-gray-900 text-lg">
                                                {formatPrice(order.total_amount)} <span className="text-sm font-normal text-gray-500">تومان</span>
                                            </div>
                                            
                                            <button 
                                                onClick={(e) => handleShare(e, order.id, 'order')}
                                                disabled={sharingId === order.id}
                                                className="p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                                                title="اشتراک‌گذاری فاکتور"
                                                aria-label="اشتراک‌گذاری فاکتور"
                                            >
                                                {sharingId === order.id ? (
                                                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <Share2 className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Progress Bar (Visual flair) */}
                                    <div className="absolute bottom-0 right-0 left-0 h-1 bg-gray-50">
                                        <div 
                                            className={`h-full ${statusConfig?.bgColor?.replace('bg-', 'bg-') || 'bg-gray-200'}`} 
                                            style={{ width: order.status === 'delivered' ? '100%' : order.status === 'shipped' ? '75%' : '25%' }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )
                ) : (
                    selections.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                            <List className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-900 mb-2">لیستی یافت نشد</h3>
                            <p className="text-gray-500 mb-6">شما هنوز هیچ لیست منتخبی ایجاد نکرده‌اید.</p>
                            <button
                                onClick={() => navigate('/products')}
                                className="bg-primary text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-light transition-colors shadow-lg shadow-primary/20"
                            >
                                مشاهده محصولات
                            </button>
                        </div>
                    ) : (
                        selections.map((selection) => (
                            <div 
                                key={selection.id} 
                                className="group bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-all duration-300 cursor-pointer"
                                onClick={() => navigate(`/selections/${selection.id}`)}
                            >
                                <div className="flex flex-col md:flex-row md:items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                                        <List className="w-6 h-6 text-green-600" />
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-gray-900">
                                                لیست <span className="font-mono text-lg ml-1">#{selection.id.slice(0, 8)}</span>
                                            </h3>
                                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                ثبت شده
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                            <span>{formatDate(selection.created_at)}</span>
                                            <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                            <span>{selection.items?.length || 0} قلم کالا</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between md:justify-end gap-4 mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-50">
                                        <div className="font-bold text-gray-900 text-lg">
                                            {selection.calculated_price 
                                                ? formatPrice(selection.calculated_price.total_price)
                                                : '---'} <span className="text-sm font-normal text-gray-500">تومان</span>
                                        </div>
                                        
                                        <button 
                                            onClick={(e) => handleShare(e, selection.id, 'selection')}
                                            disabled={sharingId === selection.id}
                                            className="p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                                            title="اشتراک‌گذاری لیست"
                                            aria-label="اشتراک‌گذاری لیست"
                                        >
                                            {sharingId === selection.id ? (
                                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <Share2 className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-10">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="صفحه قبل"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                    <span className="text-sm font-medium text-gray-600">
                        صفحه {page} از {totalPages}
                    </span>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="صفحه بعد"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrdersPage;
